/** Marketing i18n fragment — about (/about). Company / philosophy page. */

export const aboutEn = {
  meta: {
    title: "About FixControl — governance for AI-powered operations",
    description:
      "Why FixControl exists, its mission, the design principles behind it, and where it is going. AI does the preparatory work in support and engineering; your people keep every decision, and everything is recorded.",
  },
  hero: {
    eyebrow: "Company",
    title: "Governance for AI-powered operations",
    subtitle:
      "Our mission: structurally increase what organizations get out of AI — support and engineering work that is faster, more reliable, and traceable. AI does the preparatory work on top of the tools teams already use; people keep control of what ships.",
  },
  why: {
    eyebrow: "Why FixControl exists",
    title: "Operational work is fragmented, and AI made the gaps obvious",
    description:
      "Support, engineering, and deployment each live in their own tools, and people carry context between them by hand. Adding AI without a control layer only speeds the fragmentation up. AI creates real value when it has the right context, works within agreed rules, is verified, and supports human decisions — so FixControl closes these gaps with governance rather than another silo.",
    fragmented: {
      title: "Fragmented tooling",
      body: "Support desks, issue trackers, chat, CI, and deployment each hold a piece of the truth. No single place shows the whole operation.",
    },
    disconnected: {
      title: "Support disconnected from engineering",
      body: "A problem your customer reports and the fix for it live in different systems, so the path from a ticket to a verified code change is manual and lossy.",
    },
    ungoverned: {
      title: "AI without governance",
      body: "An agent that can act on production systems without policy, approval, or a record of what it did is a liability.",
    },
    traceability: {
      title: "Poor traceability",
      body: "When something ships, it is often unclear who decided it, on what evidence, and what changed as a result.",
    },
    handovers: {
      title: "Manual handovers",
      body: "Context is retyped and re-explained at every boundary between teams. That is slow, and detail gets lost along the way.",
    },
    incidents: {
      title: "Slow incident response",
      body: "Under pressure, teams stitch together tools and threads by hand — exactly when a clear, governed path matters most.",
    },
  },
  principles: {
    eyebrow: "Design principles",
    title: "The rules FixControl is built on",
    description:
      "These principles are design constraints. They decide what the product does and what it refuses to do.",
    existing: {
      title: "Built on top of existing tooling",
      body: "FixControl connects to the systems you already run — Slack, Teams, Jira, Freshdesk, GitHub, GitLab, Argo — rather than asking teams to move into a new one.",
    },
    humanApproval: {
      title: "Human approval for high-risk work",
      body: "By default, a code change, an external reply, or a deployment waits for a recorded human decision before it happens. Any autonomy is explicit, opt-in, and policy-bounded.",
    },
    explainable: {
      title: "Evidence-backed proposals",
      body: "Every proposal carries the evidence and context it rests on, so the person approving understands what they are approving — and remains responsible for that decision.",
    },
    evidence: {
      title: "Evidence first",
      body: "Proposals arrive with the facts that justify them. That evidence is frozen with the decision, exactly as the approver saw it.",
    },
    governance: {
      title: "Governance by default",
      body: "Policy, verification, and approval are on by default. There is no fast path around the controls.",
    },
    auditability: {
      title: "Auditability",
      body: "Every meaningful action records who approved it, under which role, on what evidence, and what followed. The result is an audit-ready trail.",
    },
    gitops: {
      title: "Kubernetes and GitOps friendly",
      body: "Delivery is pull-request and GitOps native. Changes ship through review and your existing pipelines, never a silent push to production.",
    },
    tenant: {
      title: "Tenant isolation",
      body: "Each organization's data is isolated at the database boundary and enforced fail-closed, so one organization can never see another's work.",
    },
    security: {
      title: "Enterprise security",
      body: "Signed webhooks, secret-safe logging, scoped admin access, and encrypted credentials come standard.",
    },
  },
  story: {
    eyebrow: "The story behind FixControl",
    title: "One pattern kept repeating: lost context, unclear decisions",
    lead:
      "FixControl came from watching the same operational failures repeat across software projects and organizations: in support, in engineering, in deployment, and in how those workflows fit together.",
    body:
      "The pattern was consistent. Capable teams were slowed by fragmented tools and manual handovers, and more recently by AI bolted on in ways that removed control rather than adding it. FixControl is built around one conclusion: make AI useful inside enterprise workflows while keeping humans in control.",
    attribution:
      "FixControl is built and operated by FixControl B.V. in Hoorn, the Netherlands, as a focused B2B SaaS company with one product: the governance layer for AI-assisted operations.",
    timelineTitle: "How the idea took shape",
    steps: {
      observation: {
        tag: "Observation",
        title: "Fragmented tools, lost context",
        body: "Very different teams hit the same problems: fragmented workflows, context lost at every handover, and no clear line from a problem to a verified fix.",
      },
      ai: {
        tag: "The AI shift",
        title: "Agents could act, ungoverned",
        body: "As AI entered these workflows, the gap became concrete. Agents could act, but nothing verified, approved, or recorded what they did.",
      },
      goal: {
        tag: "The goal",
        title: "Useful AI, with humans in control",
        body: "The aim became a governance layer: let AI propose and prepare the work, but keep policy, verification, and human approval in the path of anything consequential.",
      },
      product: {
        tag: "FixControl",
        title: "Governance became the product",
        body: "That layer became FixControl. AI drafts the work, policy and verification check it, and a person approves it. Every decision is recorded and auditable.",
      },
    },
  },
  vision: {
    eyebrow: "Where FixControl is going",
    title: "One governed layer across the whole operation",
    description:
      "The direction is a single governed layer across support, engineering, and deployment. AI takes on more of the work; accountable people keep the decisions.",
    connect: {
      title: "Connect existing systems",
      body: "Bring support, engineering, and deployment tools into one governed flow, so work moves between them without manual re-entry.",
    },
    context: {
      title: "Improve operational context",
      body: "Carry the full context of a problem — evidence, history, and decisions — alongside the work, so nothing is lost at a boundary.",
    },
    govern: {
      title: "Govern AI execution",
      body: "Keep policy, verification, and approval in the path of every AI action that touches a production system.",
    },
    incident: {
      title: "Speed up incident response",
      body: "Give teams a single path to follow under pressure, with the same checks in place, so responding faster does not mean cutting corners.",
    },
    oversight: {
      title: "Maintain human oversight",
      body: "Keep a person accountable for every consequential decision, with the evidence they need to make it well.",
    },
  },
  onboarding: {
    eyebrow: "Guided onboarding, built to scale",
    title: "Live quickly, governed from day one",
    para1:
      "FixControl connects to the systems your operation runs on — support desks, issue trackers, source repositories and deployment workflows — so every rollout gets guided onboarding: we set up the integrations, approval gates and audit boundaries together with your team, and automation is trustworthy from the first action.",
    para2:
      "The platform is designed as scalable multi-tenant SaaS. Organizations using it expand usage across seats, integrations, ticket volume and governed workflows as approval paths, audit boundaries and integration health are validated — start with one workflow or your whole support operation, the governance model is the same.",
    pilotTitle: "What a pilot is for",
    pilotBody:
      "A pilot is the fastest way to see FixControl on your own tickets and code: it defines the first workflows, integrations, approval gates and success criteria. From there, teams expand usage by adding seats, integrations, action volume and additional governed workflows.",
  },
  confidence: {
    title: "AI should increase confidence, not reduce control.",
    description:
      "Every design decision in FixControl comes back to this. The goal is not just more efficient AI — it is more output per employee in your organization, a shorter path from problem to solution, lower risk, and a demonstrably stronger business.",
    cta: "Talk to us",
  },
  company: {
    eyebrow: "Company",
    entity: "FixControl B.V.",
    location: "Hoorn, the Netherlands",
    note:
      "FixControl is built by FixControl B.V. The company builds one thing: a governance layer that keeps people in control of AI-assisted operations.",
    stage:
      "FixControl is built and operated from the Netherlands as a focused B2B SaaS company. It does one thing well: keep people in control of AI-assisted operations, from support intake to governed deployment. Every organization using FixControl works directly with the team that builds the product — through onboarding, security review and day-to-day support.",
    contactLead: "Security, procurement or fit questions:",
    contactNote:
      "You get a direct reply from the team that builds the product, typically within one business day. Procurement and security materials are available on request.",
  },
};

export const aboutNl: typeof aboutEn = {
  meta: {
    title: "Over FixControl — governance voor AI-gedreven operations",
    description:
      "Waarom FixControl bestaat, de missie, de ontwerpprincipes erachter en waar het naartoe gaat. AI doet het voorwerk in support en engineering; jouw mensen houden elke beslissing, en alles wordt vastgelegd.",
  },
  hero: {
    eyebrow: "Bedrijf",
    title: "Governance voor AI-gedreven operations",
    subtitle:
      "Onze missie: het verdienvermogen van organisaties structureel vergroten — support- en engineeringwerk dat sneller, betrouwbaarder en herleidbaar is. AI doet het voorwerk bovenop de tools die teams al gebruiken; mensen houden de controle over wat live gaat.",
  },
  why: {
    eyebrow: "Waarom FixControl bestaat",
    title: "Operationeel werk is versnipperd, en AI maakte de gaten zichtbaar",
    description:
      "Support, engineering en deployment leven elk in hun eigen tools, terwijl mensen de context er met de hand tussen dragen. AI toevoegen zonder een controlelaag versnelt die versnippering alleen maar. Echte waarde ontstaat wanneer AI de juiste context heeft, binnen afgesproken regels werkt, geverifieerd wordt en menselijke beslissingen ondersteunt — dus dicht FixControl deze gaten met governance in plaats van nóg een silo.",
    fragmented: {
      title: "Versnipperde tooling",
      body: "Supportdesks, issue trackers, chat, CI en deployment houden elk een deel van de waarheid vast, en nergens is de hele operatie te zien.",
    },
    disconnected: {
      title: "Support losgekoppeld van engineering",
      body: "Een probleem dat je klant meldt en de fix ervoor leven in verschillende systemen, waardoor de weg van ticket naar geverifieerde codewijziging handmatig en lekgevoelig is.",
    },
    ungoverned: {
      title: "AI zonder governance",
      body: "Een agent die op productiesystemen kan handelen zonder beleid, zonder goedkeuring en zonder registratie van wat het deed, is een risico.",
    },
    traceability: {
      title: "Slechte traceerbaarheid",
      body: "Als er iets live gaat, is vaak onduidelijk wie het besloot, op welk bewijs, en wat er daadwerkelijk door veranderde.",
    },
    handovers: {
      title: "Handmatige overdrachten",
      body: "Context wordt bij elke grens tussen teams opnieuw ingetypt en uitgelegd. Dat is traag, en detail gaat onderweg verloren.",
    },
    incidents: {
      title: "Trage incidentrespons",
      body: "Onder druk knopen teams tools en threads met de hand aan elkaar — juist wanneer een helder, gegoverneerd pad het meest telt.",
    },
  },
  principles: {
    eyebrow: "Ontwerpprincipes",
    title: "De regels waarop FixControl is gebouwd",
    description:
      "Deze principes zijn ontwerpbeperkingen. Ze bepalen wat het product wel doet en wat het weigert te doen.",
    existing: {
      title: "Gebouwd bovenop bestaande tooling",
      body: "FixControl koppelt met de systemen die je al draait — Slack, Teams, Jira, Freshdesk, GitHub, GitLab, Argo — in plaats van teams naar een nieuwe tool te laten verhuizen.",
    },
    humanApproval: {
      title: "Menselijke goedkeuring voor risicovol werk",
      body: "Standaard wacht een codewijziging, een extern antwoord of een deployment op een vastgelegde menselijke beslissing voordat het gebeurt. Elke autonomie is expliciet, opt-in en begrensd door beleid.",
    },
    explainable: {
      title: "Voorstellen met bewijs",
      body: "Elk voorstel draagt het bewijs en de context waarop het steunt, zodat wie goedkeurt begrijpt wat hij goedkeurt — en verantwoordelijk blijft voor die beslissing.",
    },
    evidence: {
      title: "Bewijs eerst",
      body: "Voorstellen komen binnen mét de feiten die ze rechtvaardigen. Dat bewijs wordt met de beslissing bevroren, precies zoals de goedkeurder het zag.",
    },
    governance: {
      title: "Governance standaard aan",
      body: "Beleid, verificatie en goedkeuring staan standaard aan. Er is geen snelle route om de controles heen.",
    },
    auditability: {
      title: "Auditeerbaarheid",
      body: "Elke betekenisvolle actie legt vast wie hem goedkeurde, onder welke rol, op welk bewijs, en wat erop volgde. Het resultaat is een audit-klaar spoor.",
    },
    gitops: {
      title: "Kubernetes- en GitOps-vriendelijk",
      body: "Levering is pull-request- en GitOps-native. Wijzigingen gaan via review en je bestaande pipelines, nooit via een stille push naar productie.",
    },
    tenant: {
      title: "Tenant-isolatie",
      body: "De data van elke organisatie is geïsoleerd op de databasegrens en fail-closed afgedwongen, zodat de ene organisatie het werk van de andere nooit kan zien.",
    },
    security: {
      title: "Enterprise-beveiliging",
      body: "Ondertekende webhooks, secret-veilige logging, afgebakende admin-toegang en versleutelde credentials horen er standaard bij.",
    },
  },
  story: {
    eyebrow: "Het verhaal achter FixControl",
    title: "Eén patroon bleef terugkomen: verloren context, onduidelijke beslissingen",
    lead:
      "FixControl kwam voort uit het steeds weer zien terugkomen van dezelfde operationele knelpunten bij softwareprojecten en organisaties: in support, in engineering, in deployment, en in hoe die workflows op elkaar aansluiten.",
    body:
      "Het patroon was consistent. Capabele teams werden afgeremd door versnipperde tools en handmatige overdrachten, en recenter door AI die erbovenop werd geplakt op manieren die controle wegnamen in plaats van toevoegden. FixControl is gebouwd rond één conclusie: maak AI bruikbaar binnen enterprise-workflows terwijl mensen de controle houden.",
    attribution:
      "FixControl wordt gebouwd en beheerd door FixControl B.V. in Hoorn, als gespecialiseerde B2B SaaS met één product: de governance-laag voor AI-ondersteunde operations.",
    timelineTitle: "Hoe het idee vorm kreeg",
    steps: {
      observation: {
        tag: "Observatie",
        title: "Versnipperde tools, verloren context",
        body: "Heel verschillende teams liepen tegen dezelfde problemen aan: versnipperde workflows, context die bij elke overdracht verloren ging, en geen heldere lijn van probleem naar geverifieerde fix.",
      },
      ai: {
        tag: "De AI-verschuiving",
        title: "Agents konden handelen, ongegoverneerd",
        body: "Toen AI deze workflows binnenkwam, werd het gat concreet. Agents konden handelen, maar niets verifieerde, keurde goed of legde vast wat ze deden.",
      },
      goal: {
        tag: "Het doel",
        title: "Bruikbare AI, met mensen aan het roer",
        body: "Het doel werd een governance-laag: laat AI het werk voorstellen en voorbereiden, maar houd beleid, verificatie en menselijke goedkeuring in het pad van alles met impact.",
      },
      product: {
        tag: "FixControl",
        title: "Governance werd het product",
        body: "Die laag werd FixControl. AI stelt het werk op, beleid en verificatie controleren het, en een mens keurt het goed. Elke beslissing wordt vastgelegd en is auditeerbaar.",
      },
    },
  },
  vision: {
    eyebrow: "Waar FixControl naartoe gaat",
    title: "Eén gegoverneerde laag over de hele operatie",
    description:
      "De richting is één gegoverneerde laag over support, engineering en deployment. AI neemt meer van het werk over; verantwoordelijke mensen houden de beslissingen.",
    connect: {
      title: "Bestaande systemen verbinden",
      body: "Breng support-, engineering- en deployment-tools samen in één gegoverneerde flow, zodat werk ertussen beweegt zonder handmatige herinvoer.",
    },
    context: {
      title: "Operationele context verbeteren",
      body: "Draag de volledige context van een probleem — bewijs, historie en beslissingen — mee met het werk, zodat er niets bij een grens verloren gaat.",
    },
    govern: {
      title: "AI-uitvoering governen",
      body: "Houd beleid, verificatie en goedkeuring in het pad van elke AI-actie die een productiesysteem raakt.",
    },
    incident: {
      title: "Incidentrespons versnellen",
      body: "Geef teams onder druk één pad om te volgen, met dezelfde controles op hun plek, zodat sneller reageren niet betekent dat er hoeken worden afgesneden.",
    },
    oversight: {
      title: "Menselijk toezicht behouden",
      body: "Houd een mens verantwoordelijk voor elke beslissing met impact, mét het bewijs dat nodig is om haar goed te nemen.",
    },
  },
  onboarding: {
    eyebrow: "Begeleide onboarding, gebouwd om te schalen",
    title: "Snel live, governed vanaf dag één",
    para1:
      "FixControl koppelt met de systemen waar je operatie op draait — supportdesks, issue trackers, source repositories en deployment-workflows — dus elke uitrol krijgt begeleide onboarding: we richten de integraties, goedkeuringspoorten en auditgrenzen samen met je team in, zodat de automatisering vanaf de eerste actie te vertrouwen is.",
    para2:
      "Het platform is ontworpen als schaalbare multi-tenant SaaS. Organisaties die het gebruiken, breiden hun gebruik uit over gebruikers, integraties, ticketvolume en gegoverneerde workflows terwijl approval paths, audit boundaries en integration health worden gevalideerd — begin met één workflow of met je hele supportoperatie, het governancemodel is hetzelfde.",
    pilotTitle: "Waar een pilot voor dient",
    pilotBody:
      "Een pilot is de snelste manier om FixControl op je eigen tickets en code te zien: hij bepaalt de eerste workflows, integraties, goedkeuringspoorten en succescriteria. Van daaruit breiden teams hun gebruik uit met extra gebruikers, integraties, aantal AI-acties en aanvullende gegoverneerde workflows.",
  },
  confidence: {
    title: "AI hoort vertrouwen te vergroten, niet controle te verkleinen.",
    description:
      "Elke ontwerpkeuze in FixControl komt hierop terug. Het doel is niet alleen efficiëntere AI — het is meer resultaat per medewerker in je organisatie, een kortere weg van probleem naar oplossing, lagere risico's en een aantoonbaar sterker verdienvermogen.",
    cta: "Neem contact op",
  },
  company: {
    eyebrow: "Bedrijf",
    entity: "FixControl B.V.",
    location: "Hoorn, Nederland",
    note:
      "FixControl wordt gebouwd door FixControl B.V. Het bedrijf bouwt aan één ding: een governance-laag die mensen in controle houdt over AI-ondersteunde operaties.",
    stage:
      "FixControl wordt vanuit Nederland gebouwd en beheerd als gespecialiseerde B2B SaaS. Het doet één ding goed: mensen in controle houden over AI-ondersteunde operaties, van support-intake tot governed deployment. Elke organisatie die FixControl gebruikt, werkt rechtstreeks met het team dat het product bouwt — bij onboarding, security-review en dagelijkse support.",
    contactLead: "Vragen over security, procurement of fit:",
    contactNote:
      "Je krijgt direct antwoord van het team dat het product bouwt, doorgaans binnen één werkdag. Procurement- en securitymateriaal is op aanvraag beschikbaar.",
  },
};
